import React from 'react';
import { Box, useTheme, type SxProps, type Theme } from '@mui/material';

export type PaddiqLogoVariant = 'brand' | 'header' | 'sidebar';

const ICON_ASPECT = 341 / 306;
const ACCENT = '#9A28FE';
const WORDMARK = 'PADDIQ';

const scales = {
  brand: {
    iconHeight: 64,
    wordmarkSize: '2.625rem',
    taglineSize: '0.72rem',
    taglineTracking: '0.1em',
    wordmarkTracking: '0.08em',
    gap: 1.5,
    textGap: 0.45,
    showTagline: true,
  },
  header: {
    iconHeight: 28,
    wordmarkSize: '0.8125rem',
    taglineSize: '0.72rem',
    taglineTracking: '0.12em',
    wordmarkTracking: '0.12em',
    gap: 1,
    textGap: 0,
    showTagline: false,
  },
  sidebar: {
    iconHeight: 24,
    compactIconHeight: 20,
    wordmarkSize: '0.875rem',
    taglineSize: '0.72rem',
    taglineTracking: '0.12em',
    wordmarkTracking: '0.08em',
    gap: 0.75,
    textGap: 0,
    showTagline: false,
    matchIconHeight: true,
  },
} as const;

export interface PaddiqLogoProps {
  variant?: PaddiqLogoVariant;
  iconSrc?: string;
  /** Icon only — for collapsed sidebar and other tight spaces. */
  compact?: boolean;
  /** Optional wordmark color for branded surfaces with a fixed background. */
  wordmarkColor?: string;
  /** Optional wordmark size override for surfaces that need stronger brand presence. */
  wordmarkSize?: string;
  sx?: SxProps<Theme>;
}

function WordmarkLetters({
  fontSize,
  letterSpacing,
  matchIconHeight = false,
  paddiColor,
}: {
  fontSize: string;
  letterSpacing: string;
  matchIconHeight?: boolean;
  paddiColor: string;
}) {
  const wordmarkStyle = {
    display: 'inline-block',
    fontWeight: 900,
    fontStyle: 'italic',
    fontSize,
    lineHeight: matchIconHeight ? 1 : 1.05,
    letterSpacing,
    whiteSpace: 'nowrap',
  } as const;

  return (
    <Box component="span" aria-label={WORDMARK} sx={wordmarkStyle}>
      <Box component="span" aria-hidden sx={{ color: paddiColor, display: 'inline' }}>
        PADDI
      </Box>
      <Box component="span" aria-hidden sx={{ color: ACCENT, display: 'inline', letterSpacing: 0 }}>
        Q
      </Box>
    </Box>
  );
}

export function PaddiqLogo({
  variant = 'brand',
  iconSrc = '/ICON.png',
  compact = false,
  wordmarkColor,
  wordmarkSize,
  sx,
}: PaddiqLogoProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Auth/marketing surfaces use the brand variant on a fixed dark background.
  const paddiColor = wordmarkColor ?? (variant === 'brand' || isDark ? '#fff' : '#1F2937');
  const scale = scales[variant];
  const iconHeight =
    compact && 'compactIconHeight' in scale && scale.compactIconHeight
      ? scale.compactIconHeight
      : scale.iconHeight;
  const iconWidth = iconHeight * ICON_ASPECT;
  const matchIconHeight = 'matchIconHeight' in scale && scale.matchIconHeight;
  const wordmarkFontSize =
    wordmarkSize ?? (matchIconHeight ? `${iconHeight}px` : scale.wordmarkSize);

  if (compact) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          ...sx,
        }}
      >
        <Box
          component="img"
          src={iconSrc}
          alt=""
          aria-hidden
          sx={{
            width: iconWidth,
            height: iconHeight,
            objectFit: 'contain',
            flexShrink: 0,
            display: 'block',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: scale.gap,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={iconSrc}
        alt=""
        aria-hidden
        sx={{
          width: iconWidth,
          height: iconHeight,
          objectFit: 'contain',
          flexShrink: 0,
          display: 'block',
        }}
      />
      <Box
        sx={{
          display: 'inline-grid',
          gridTemplateColumns: '1fr',
          justifyContent: 'center',
        }}
      >
        {scale.showTagline ? (
          <Box
            aria-hidden
            sx={{
              gridArea: '1 / 1',
              visibility: 'hidden',
              fontWeight: 600,
              fontStyle: 'italic',
              fontSize: scale.taglineSize,
              letterSpacing: scale.taglineTracking,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            TRACK DATA. REAL INSIGHT. EVERY LAP.
          </Box>
        ) : null}
        <Box
          sx={{
            gridArea: '1 / 1',
            display: 'flex',
            flexDirection: 'column',
            gap: scale.textGap,
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <WordmarkLetters
            fontSize={wordmarkFontSize}
            letterSpacing={scale.wordmarkTracking}
            matchIconHeight={Boolean(matchIconHeight)}
            paddiColor={paddiColor}
          />
          {scale.showTagline ? (
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                fontStyle: 'italic',
                fontSize: scale.taglineSize,
                lineHeight: 1.35,
                letterSpacing: scale.taglineTracking,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              <Box component="span" sx={{ color: '#fff' }}>
                TRACK DATA. REAL INSIGHT. EVERY{' '}
              </Box>
              <Box component="span" sx={{ color: ACCENT }}>
                LAP.
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
